// Aprire e salvare file come un'app nativa, dove il browser lo permette.
//
// La File System Access API (`showOpenFilePicker`, `showSaveFilePicker`) esiste
// SOLO in secure context: https, oppure localhost. Questo strumento si
// raggiunge anche da un indirizzo di rete in chiaro — e li' quelle funzioni
// non ci sono proprio, non e' che falliscono.
//
// Quindi due modi, dichiarati invece che sperati:
//
//   NATIVO   si sceglie il file, e da li' in poi `Salva` riscrive QUEL file.
//   SCIALUPPA  si apre con un <input type=file> e si «salva» scaricando.
//              Nessun salvataggio sul posto: il browser non ce lo lascia fare.
//
// La UI dice quale dei due e' attivo: un `Salva` che in un caso riscrive il
// file e nell'altro scarica un doppione, senza dirlo, e' una bugia.

export interface Aperto
{
    nome: string;
    testo: string;
    handle: FileSystemFileHandle | null;
}


const TIPI = [{
    description: "Markdown",
    accept: { "text/markdown": [".md", ".markdown", ".mdown"] },
}];


export function nativoDisponibile(): boolean
{
    return typeof window.showOpenFilePicker === "function" && window.isSecureContext;
}


export async function apri(): Promise<Aperto | null>
{
    const scegli = window.showOpenFilePicker;
    if (scegli !== undefined && window.isSecureContext)
    {
        const [handle] = await scegli({ types: TIPI, multiple: false }).catch(() => []);
        if (handle === undefined) { return null; }

        const file = await handle.getFile();
        return { nome: file.name, testo: await file.text(), handle };
    }

    // La scialuppa: un input nascosto, usato e buttato.
    return await new Promise((resolve) =>
    {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".md,.markdown,.mdown,text/markdown";
        input.addEventListener("change", () =>
        {
            const file = input.files?.[0];
            if (file === undefined) { resolve(null); return; }
            void file.text().then((testo) => resolve({ nome: file.name, testo, handle: null }));
        });
        input.addEventListener("cancel", () => resolve(null));
        input.click();
    });
}


// Riscrive il file aperto. Vale solo col modo nativo: senza handle non c'e'
// nessun «quel file» da riscrivere, e chi chiama deve saperlo.
export async function salvaSuHandle(handle: FileSystemFileHandle, testo: string): Promise<void>
{
    const scrittura = await handle.createWritable();
    await scrittura.write(testo);
    await scrittura.close();
}


export async function salvaCome(nome: string, testo: string): Promise<FileSystemFileHandle | null>
{
    const scegli = window.showSaveFilePicker;
    if (scegli !== undefined && window.isSecureContext)
    {
        const handle = await scegli({ suggestedName: nome, types: TIPI }).catch(() => null);
        if (handle === null) { return null; }

        await salvaSuHandle(handle, testo);
        return handle;
    }

    scarica(new Blob([testo], { type: "text/markdown;charset=utf-8" }), nome);
    return null;
}


export function scarica(dato: Blob, nome: string): void
{
    const url = URL.createObjectURL(dato);
    const a = document.createElement("a");
    a.href = url;
    a.download = nome;
    a.click();

    // Revocare SUBITO annullerebbe il download in alcuni browser: si lascia
    // vivere il tempo di partire, poi si libera.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}


// «documento.md» → «documento.pdf». Il nome del PDF segue quello del sorgente:
// scaricare venti «documento.pdf» tutti uguali non aiuta nessuno.
export function nomePdf(nomeMd: string): string
{
    return `${nomeMd.replace(/\.(md|markdown|mdown)$/i, "")}.pdf`;
}
